import dayjs from 'dayjs'
import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { message, Row, Col, Alert, Tabs } from 'antd'

import { PageHeader } from '../Common'
import { toPath } from '../../routes'

import { usePractice } from '../../redux/slices/practices'
import { useFeatureGuard } from '../../redux/slices/session'

import api from '../../api'
import { useIsMobile } from '../../hooks'
import useInterval from '../../hooks/useInterval'

import { getPdfUrlFromPkg } from '../../lib/file'

import Loading from '../Loading'
import PDFPreview from '../Common/PDFPreview'
import OfficeCheckForm from './Form'
import CreateOfficeCheck from './UseCase/CreateOfficeCheck'
import ReportUpload from './ReportUpload'

const POLLING_INTERVAL = 3000

const { TabPane } = Tabs

export default function NewOfficeCheck() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const [patient, setPatient] = useState()
  const [pdfUrl, setPdfUrl] = useState()
  const [sessionFileKey, setSessionFileKey] = useState('')
  const { practiceId } = useParams()
  const practice = usePractice(practiceId)

  const [sessionFiles, setSessionFiles] = useState({})

  const [isPollingPdfExtraction, setIsPollingPdfExtraction] = useState(false)
  const [, setPollingRetryCount] = useState(0)
  const [processingMessage, setProcessingMessage] = useState(
    'Processing device report...',
  )

  const [officeCheckId, setOfficeCheckId] = useState()
  const [redirectUrl, setRedirectUrl] = useState()

  const stopPollingAndReset = useCallback(() => {
    setIsPollingPdfExtraction(false)
    setPollingRetryCount(0)
  }, [])

  const handleRetryCount = useCallback(onMaxRetries => {
    setPollingRetryCount(prevCount => {
      const newCount = prevCount + 1
      if (newCount === 10) {
        setProcessingMessage(
          'This is taking a bit longer than expected. Please wait a few more seconds.',
        )
      } else if (newCount >= 100) {
        onMaxRetries()
      }
      return newCount
    })
  }, [])

  const showExtractionFailure = useCallback(() => {
    stopPollingAndReset()
    message.error('PDF extraction failed. Please fill out the form manually.')
    navigate(redirectUrl)
  }, [navigate, stopPollingAndReset, redirectUrl])

  const checkPdfExtractionStatus = useCallback(async () => {
    try {
      const officeCheck = await api.officeChecks.get(practiceId, officeCheckId)

      const status = officeCheck.pdfExtractionStatus

      if (status === 'complete') {
        stopPollingAndReset()
        message.success('PDF extraction completed successfully')
        navigate(redirectUrl)
      } else if (status === 'failed') {
        showExtractionFailure()
      } else {
        handleRetryCount(showExtractionFailure)
      }
    } catch (err) {
      handleRetryCount(showExtractionFailure)
    }
  }, [
    officeCheckId,
    practiceId,
    stopPollingAndReset,
    showExtractionFailure,
    handleRetryCount,
    redirectUrl,
    navigate,
  ])

  useInterval(
    checkPdfExtractionStatus,
    isPollingPdfExtraction ? POLLING_INTERVAL : null,
  )

  const onSubmit = useCallback(
    async (payload, { setErrors }) => {
      const {
        patientId,
        firstName,
        lastName,
        dateOfBirth,
        practiceMrn,
        deviceModelId,
        deviceSerialNumber,
        dateOfCheck,
        remoteMonitoring,
      } = payload
      const showErrors = errors =>
        typeof errors === 'string' ? message.error(errors) : setErrors(errors)

      const isToday = dateOfCheck && dateOfCheck.isSame(dayjs(), 'day')

      try {
        const [patientCreationResult, preAuthorizationCreationResult] =
          await CreateOfficeCheck(patient?.id || patientId, practiceId, {
            firstName,
            lastName,
            dateOfBirth,
            practiceMrn,
            deviceModelId,
            deviceSerialNumber,
            dateOfCheck,
            remoteMonitoring,
          })
        if (
          patientCreationResult.isSuccess() &&
          preAuthorizationCreationResult.isSuccess()
        ) {
          setPatient(undefined)

          const { id } = preAuthorizationCreationResult.object

          const url = `${
            isToday
              ? toPath.practices.officeChecks.officeCheck.edit({
                  practiceId,
                  id: preAuthorizationCreationResult.object.id,
                })
              : toPath.practices.officeChecks({ practiceId })
          }`

          setRedirectUrl(url)

          if (sessionFileKey && sessionFiles[sessionFileKey]?.length) {
            const data = new FormData()

            sessionFiles[sessionFileKey].forEach(file =>
              data.append('attachment[]', file),
            )

            api.officeChecks
              .processDeviceReport(id, practiceId, data)
              .then(response => {
                if (response.pdfExtractionStatus !== 'skipped') {
                  setOfficeCheckId(id)
                  setPollingRetryCount(0) // Reset retry count when starting new polling
                  setIsPollingPdfExtraction(true)
                } else {
                  message.success('Office check successfully created')
                  navigate(url, { replace: true })
                }
              })
          } else {
            message.success('Office check successfully created')
            navigate(url, { replace: true })
          }

          return
        }

        if (!patientCreationResult.isSuccess()) {
          showErrors(patientCreationResult.getErrors())

          return
        }

        if (!preAuthorizationCreationResult.isSuccess()) {
          setPatient(patientCreationResult.object)
          showErrors(preAuthorizationCreationResult.getErrors())
        }
      } catch (e) {
        message.error(`Something went wrong. ${e.message}`)
        throw e
      }
    },
    [patient, practiceId, sessionFileKey, sessionFiles, navigate],
  )

  useFeatureGuard('in_office_checks')

  const onReportUpload = async payload => {
    // We expect:
    // - a single PKG file,
    // - a single PDF file, or
    // - a PDF and a LOG file
    const pkg = payload.find(f => f.name.toLowerCase().endsWith('.pkg'))
    const pdf = payload.find(f => f.name.toLowerCase().endsWith('.pdf'))

    if (!pdf && !pkg) return

    const payloadKey = `iocfile_${Date.now()}_${practiceId}`

    // Generate the preview URL
    let previewUrl = null
    if (pkg) {
      // Assuming this returns a Blob or Blob URL
      previewUrl = await getPdfUrlFromPkg(pkg)
    } else if (pdf) {
      // Use native blob URL instead of base64
      previewUrl = URL.createObjectURL(pdf)
    }

    setPdfUrl(previewUrl)

    // Keep only relevant files
    const files = payload.filter(f =>
      ['pdf', 'pkg', 'log'].includes(f.name.split('.').pop().toLowerCase()),
    )

    // Store the actual File objects in memory
    setSessionFiles(prev => ({
      ...prev,
      [payloadKey]: files,
    }))

    // Keep the current key for retrieval later
    setSessionFileKey(payloadKey)
  }

  if (isPollingPdfExtraction) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '50vh',
        }}
      >
        <Loading large />
        <div style={{ marginTop: '16px', fontSize: '16px', color: '#666' }}>
          {processingMessage}
        </div>
      </div>
    )
  }

  const reportPane = (
    <>
      <Alert
        message="Please verify populated fields for accuracy, as discrete data from the device may not match the PDF exactly."
        banner
        closable
        style={{ marginBottom: '1em' }}
      />
      {!pdfUrl && <ReportUpload onUpload={onReportUpload} />}
      {pdfUrl && <PDFPreview url={pdfUrl} perPage={1} hidePagination />}
    </>
  )

  const formPane = (
    <OfficeCheckForm practiceId={practiceId} onSubmit={onSubmit} />
  )

  return (
    <div>
      <PageHeader title="Add Office Check" subTitle={practice?.name} />
      {isMobile ? (
        // Half a phone is not enough for either side: the drop zone loses its
        // hit area and the form loses its inputs. Same two panes, one at a time.
        <Tabs defaultActiveKey="report">
          <TabPane tab="Report" key="report">
            {reportPane}
          </TabPane>
          <TabPane tab="Patient" key="patient">
            {formPane}
          </TabPane>
        </Tabs>
      ) : (
        <Row gutter={{ sm: 12 }}>
          <Col span={12}>{reportPane}</Col>
          <Col span={12}>{formPane}</Col>
        </Row>
      )}
    </div>
  )
}
